import qs from 'qs'
import { KEYWORD_LISTS_ENDPOINT } from 'constants/api'
import {
  buildFullPath,
  buildFullPathClean,
  buildFilterQuery
} from './build_paths'

import { get, post, put, destroy } from './api/helpers'

/*
 *               _
 *              | |
 *           ___| |_ ___  _ __
 *          / __| __/ _ \| '_ \
 *          \__ \ || (_) | |_) |
 *          |___/\__\___/| .__/
 *                       | |
 *                       |_|
 *
 * Please do not add to this file.
 * Additions to this file will get pulled into the main JS bundle where they probably don't need to be
 * Please create a new file, add your methods/constants to that and import just the code you need.
 *
 * For more info see https://github.com/Junglescout/docs/blob/master/client/Code-Splitting.md
 */

import academy from './api/academy'
import accounts from './api/accounts'
import admin from './api/admin'
import adminUsers from './api/admin/users'
import emailUnsubscribeLink from './api/email_unsubscribe_link'
import product from './api/product'
import productCosts from './api/product_costs'
import salesAnalytics from './api/sales_analytics'
import seats from './api/seats'
import sessions from './api/sessions'
import settings from './api/settings'
import subscriptionItems from './api/subscription_items'
import users from './api/users'

const RESCRAPE_TIMEOUT = 300000 // 5 minutes - because each scrape can take longer than our existing timeouts (also in dev env)
const LONG_FETCH_TIMEOUT = 120000 // 2 minutes - to support China, where the firewall causes a different magnitude of delays

export default {
  fetchSellerPlans() {
    const path = 'api/available_seller_plans'
    return get(path)
  },

  getAdminBasePlans(params) {
    const path = buildFullPath(
      'api/admin/subscription_plans/base_plans',
      params
    )
    return get(path)
  },

  getAdminCampaigns(params) {
    const path = buildFullPath('api/admin/subscription_plans/campaigns', params)
    return get(path)
  },

  updateAdminSubscriptionPlan(basePlanId, data) {
    const path = `api/admin/subscription_plans/${basePlanId}`
    return put(path, { data })
  },

  removePaymentMethod(params) {
    const path = buildFullPath('api/admin/remove_payment_method', params)
    return get(path)
  },

  createAdminSubscriptionPlan(data) {
    const path = `api/admin/subscription_plans`
    return post(path, { data })
  },

  alipaySetupIntent(data) {
    const path = `api/accounts/alipay_setup_intent`
    return post(path, { data })
  },

  deleteAdminBasePlan(data) {
    const path = `api/admin/subscription_plans/${data.id}`
    return destroy(path, { data })
  },

  duplicateAdminSubscriptionPlan(planId, planType) {
    const path = `api/admin/subscription_plans/${planId}/duplicate?entity_type=${planType}`
    return post(path)
  },

  viewAdminSubscriptionPlan(planId, planType) {
    const path = `api/admin/subscription_plans/${planId}?entity_type=${planType}`
    return get(path)
  },

  deleteAdminSubscriptionPlan(planId, planType) {
    const path = `api/admin/subscription_plans/${planId}?entity_type=${planType}`
    return destroy(path)
  },

  adminAsinRescrape(data) {
    const path = buildFullPath('api/admin/products/rescrape', data)
    return post(path, { data, timeout: RESCRAPE_TIMEOUT })
  },

  adminGetUsers(data) {
    const path = buildFullPath('api/admin/users', data)
    return get(path)
  },

  adminGetJungleScoutUsers() {
    const path = 'api/admin/junglescout_users'
    return get(path)
  },

  adminGetJungleScoutUserVersions(data) {
    const path = buildFullPath('api/admin/junglescout_user/versions', data)
    return get(path)
  },

  adminGetAdminPermissionsLookup() {
    const path = 'api/admin/permissions_lookup'
    return get(path)
  },

  adminEmailUsersCsv(data) {
    const path = 'api/admin/email_users_csv'
    return post(path, { data })
  },

  adminGetUserPlans() {
    const path = 'api/admin/user_plan_dropdown'
    return get(path)
  },

  adminRolesCsv() {
    const path = 'api/admin/admin_roles_csv_export'
    return get(path)
  },

  adminGetUser(userId) {
    const path = `api/admin/user?user_id=${userId}`
    return get(path)
  },

  adminCreateUser(data) {
    const path = `api/admin/create_user`
    return post(path, { data })
  },

  adminUpdateUser(data) {
    const path = `api/admin/update_user`
    return put(path, { data })
  },

  stripeBalanceTransaction(data) {
    const path = `api/admin/user/stripe_balance_transaction`
    return put(path, { data })
  },

  adminUpdateUserMembership(data) {
    const path = `api/admin/update_user_membership`
    return put(path, { data })
  },

  adminCreateNote(data) {
    const path = 'api/admin/admin_notes'
    return post(path, { data })
  },

  adminUpdateNote(noteId, data) {
    const path = `api/admin/admin_notes/${noteId}`
    return put(path, { data })
  },

  adminRefundStripeCharge(data) {
    const path = 'api/admin/stripe_refund'
    return post(path, { data })
  },

  adminAddAdditionalUser(data) {
    const path = 'api/admin/add_additional_user'
    return post(path, { data })
  },

  adminRevokeAdditionalUser(data) {
    const path = 'api/admin/revoke_additional_user'
    return post(path, { data })
  },

  adminUpdateAccessRights(data) {
    const path = 'api/admin/update_access_rights'
    return put(path, { data })
  },

  adminRemoveAccessRights(data) {
    const path = 'api/admin/remove_access_rights'
    return destroy(path, { data })
  },

  adminRestoreProductTracks(data) {
    const path = 'api/admin/restore_product_tracks'
    return post(path, { data })
  },

  adminCancelMembership(data) {
    const path = 'api/admin/cancel_membership'
    return post(path, { data })
  },

  adminReactivateMembership(data) {
    const path = 'api/admin/reactivate_membership'
    return post(path, { data })
  },

  adminDeleteApiSubscription(data) {
    const path = 'api/admin/delete_api_subscription'
    return post(path, { data })
  },

  adminResetApiUsage(data) {
    const path = `api/admin/reset_api_limit_counts_and_usage`
    return put(path, { data })
  },

  adminDeleteMembership(data) {
    const path = 'api/admin/delete_membership'
    return post(path, { data })
  },

  adminBanUser(data) {
    const path = 'api/admin/ban_user'
    return post(path, { data })
  },

  reset2FA(data) {
    const path = `api/admin/reset_2fa`
    return post(path, { data })
  },

  adminChangeCheckoutType(data) {
    const path = `api/admin/change_checkout_type`
    return post(path, { data })
  },

  adminGetJunglescoutUsers(data) {
    const path = buildFullPath('api/admin/junglescout_users', data)
    return get(path)
  },

  fetchStripePlans() {
    const path = 'api/admin/stripe_plans'
    return get(path)
  },

  loadGlobalData() {
    const path = `api/global_data`
    return get(path)
  },

  loadAccountData() {
    const path = `api/account_data`
    return get(path)
  },

  stripeUpgradeLink(data) {
    const path = buildFullPathClean('api/edit_subscription', data)
    return get(path)
  },

  billingInfoLink() {
    const path = 'api/billing_info'
    return get(path)
  },

  registerAuthy(data) {
    const path = `api/authy/register`
    return post(path, { data })
  },

  deleteAuthy() {
    const path = `api/authy/delete`
    return destroy(path)
  },

  verifyAuthyToken(data) {
    const path = `api/authy/verify`
    return post(path, { data })
  },

  getRegistrationInfo(data) {
    const path = `api/new_registration`
    return post(path, { data, timeout: LONG_FETCH_TIMEOUT }) // TODO: maintain separate timeouts per-locale, ie longer for China
  },

  checkRegistrationStatus(data) {
    const path = `api/check_registration_status`
    return post(path, { data })
  },

  captureEmail(data) {
    const path = `api/capture_email`
    return post(path, { data })
  },

  checkEligibility(key, token) {
    const path = `api/check_eligibility?${key}=${token}`
    return get(path)
  },

  registerUser(data) {
    const path = `api/create_registration`
    return post(path, { data, timeout: LONG_FETCH_TIMEOUT }) // TODO: maintain separate timeouts per-locale, ie longer for China
  },

  registerUserWithoutPayment(data) {
    const path = `api/no_payment_registration`
    return post(path, { data, timeout: LONG_FETCH_TIMEOUT })
  },

  existingUserCheckoutPurchase(data) {
    const path = `api/in_app_purchase`
    return post(path, { data })
  },

  registerAdditionalUser(data) {
    const path = `api/register_additional_user`
    return post(path, { data })
  },

  addAdditionalUser(data) {
    const path = `api/add_additional_user`
    return post(path, { data })
  },

  getUpgradePlans() {
    const path = `api/upgrade_plans`
    return get(path)
  },

  getRelatedKeywordsCsvJobStatus(jobId) {
    return get(
      `api/keyword_engine/related_keywords_csv_job_status?job_id=${jobId}`
    )
  },

  saveKeywordPreset(data) {
    let path = `api/keywords/save_preset?${buildFilterQuery(data.filters)}`
    path += `&name=${data.presets.name}`
    path += `&country=${data.data.selectedCountry}`
    path += `&is_private=${data.presets.is_private}`
    return get(path)
  },

  loadKeywordPreset(data) {
    const path = `api/keywords/load_preset`
    return post(path, { data })
  },

  deleteKeywordPreset(data) {
    const path = `api/keywords/delete_preset`
    return post(path, { data })
  },

  createKeywordList(data) {
    return post(KEYWORD_LISTS_ENDPOINT, { data })
  },

  getProductsByAsin(country, asins) {
    const queryParams = qs.stringify({ country, asins: asins.join() })
    const path = `api/products/get_top_products?${queryParams}`

    return get(path)
  },

  getSimilarProducts(data, signal) {
    const path = `api/products/similar?${buildFilterQuery(
      data.similarProductFilters
    )}`
    return get(path, { signal })
  },

  saveDatabasePreset(data) {
    let path = `api/products/save_preset?${buildFilterQuery(data.filters)}`
    path += `&name=${data.name}`
    path += `&country=${data.country}`
    path += `&is_private=${data.isPrivate}`
    return get(path)
  },

  loadDatabasePreset(data) {
    const path = `api/products/load_preset`
    return post(path, { data })
  },

  deleteDatabasePreset(data) {
    const path = `api/products/delete_preset`
    return post(path, { data })
  },

  updateTrackerCategory(data) {
    const path = 'api/tracker_category'
    return put(path, { data })
  },

  deleteTrackerCategory(data) {
    const path = 'api/tracker_category'
    return destroy(path, { data })
  },

  updateJspupWarning(data) {
    const path = 'api/onboarding/update_jspup_warning'
    return post(path, { data })
  },

  uploadAccountPhoto(data) {
    const path = 'api/account_photo'
    return post(path, { data, multipart: true })
  },

  deleteAccountPhoto(data) {
    const path = 'api/account_photo'
    return destroy(path, { data })
  },

  updateProfile(data) {
    const path = 'api/update_profile'
    return put(path, { data })
  },

  updatePassword(data) {
    const path = 'api/update_password'
    return put(path, { data })
  },

  inviteUsers(data) {
    const path = 'api/invite_users'
    return post(path, { data })
  },

  resendInvite(data) {
    const path = 'api/resend_invite'
    return post(path, { data })
  },

  revokeUser(data) {
    const path = 'api/revoke_user'
    return post(path, { data })
  },

  updateInvoiceInfo(data) {
    const path = 'api/update_invoice_info'
    return put(path, { data })
  },

  getInvoices(data) {
    const path = `api/invoices?page=${data.page}`
    return get(path, { data })
  },

  cancelMembership(data) {
    const path = 'api/cancel_membership'
    return put(path, { data })
  },

  reactivateMembership(data) {
    const path = 'api/reactivate_subscriptions'
    return put(path, { data })
  },

  confirmMembership(data) {
    const path = 'api/confirm_membership_paid'
    return put(path, { data })
  },

  deleteMembership(data) {
    const path = 'api/delete_membership'
    return destroy(path, { data })
  },

  removeCard(data) {
    const path = 'api/remove_credit_card'
    return put(path, { data })
  },

  updateCard(data) {
    const path = 'api/update_credit_card'
    return put(path, { data })
  },

  sendAnonymousMetrics(data) {
    const path = 'api/make_anonymous_metric'
    return post(path, { data })
  },

  downloadInvoice(data) {
    const path = `api/download_invoice`
    return post(path, { data, responseType: 'blob' })
  },

  downloadMultipleInvoices(data) {
    const path = `api/download_multiple_invoices`
    return post(path, { data, responseType: 'blob' })
  },

  emailInvoices(data) {
    const path = 'api/email_invoices'
    return post(path, { data })
  },

  adminGetProduct(data) {
    const path = `api/admin/products?country=${data.country}&asin=${data.asin}`
    return get(path)
  },

  adminGetKeyword(data) {
    const path = `api/admin/keywords?country=${data.country}&keyword=${data.keyword}`
    return get(path)
  },

  fetchProductDetails(data) {
    let path = `api/product_details/${data.asin}`
    path += data.country ? `?country=${data.country}` : ''
    return get(path)
  },

  createUserLesson(data) {
    const path = `api/user_lessons`
    return post(path, { data })
  },

  updateUserLesson(data) {
    const path = `api/user_lessons/${data.id}`
    return put(path, { data })
  },

  createUserCourse(data) {
    const path = `api/user_courses`
    return post(path, { data })
  },

  ...academy,
  ...accounts,
  ...admin,
  ...adminUsers,
  ...emailUnsubscribeLink,
  ...product,
  ...productCosts,
  ...salesAnalytics,
  ...seats,
  ...sessions,
  ...settings,
  ...subscriptionItems,
  ...users
}
